import React from 'react'
import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimesCircle, faPlus, faEyeSlash } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Checkbox, ListItem, SortableList, Text,
} from '@domain-group/suit-theme'

import useCampaignQuoteSection from '@hooks/useCampaignQuoteSection'
import useSelector from '@hooks/useSelector'

import handleCallbackAction from '@functions/handleCallbackAction'

import type { CampaignQuoteSectionModel } from '@domain-group/realhub-backend-api-types'
import type { CampaignQuoteTemplateModel } from '@domain-group/realhub-backend-api-types'

import HintWrapper from '@components/HintWrapper'

import CampaignQuoteItem from '../CampaignQuoteItem'

interface CampaignQuoteSectionProps {
  campaignQuoteSection: CampaignQuoteSectionModel,
  campaignQuoteSectionIds: number[],
  campaignQuoteTemplate: CampaignQuoteTemplateModel,
  first: boolean,
  index: number,
  last: boolean,
  lastIndex: number,
  sortedCampaignQuoteItemIds: number[],
}

const CampaignQuoteSection = React.forwardRef<HTMLDivElement, CampaignQuoteSectionProps>((props, ref) => {
  const {
    campaignQuoteSection,
    campaignQuoteSectionIds,
    campaignQuoteTemplate: { disable_custom_items, disable_section_editing },
    first,
    index,
    last,
    lastIndex,
    sortedCampaignQuoteItemIds,
  } = props

  const campaignQuoteSectionPayload = useCampaignQuoteSection(campaignQuoteSection)
  const {
    callbacks: {
      addCustomItem,
      addOrderableItem,
      addSectionAtIndex,
      createOrEditCampaignQuoteSection,
    },
    campaignQuoteSection: {
      footer,
      header,
      id,
      title,
    },
    hideDates,
    hidePrices,
    showSubtotal,
    subtotal,
  } = campaignQuoteSectionPayload

  const campaignQuoteItems = useSelector(reduxState => reduxState.entities.campaignQuoteItems)

  return (
    <Box flexDirection="column" padding="large" ref={ref}>
      <ListItem
        boxProps={{ paddingY: 'large' }}
        disableHover={disable_section_editing}
        onClick={disable_section_editing ? undefined : () => createOrEditCampaignQuoteSection({
          first, index, last, campaignQuoteSectionIds,
        })}
        showAngleIcon
      >
        <Text fontSize="large">{title}</Text>

        <Text fontSize="large" marginLeft="auto">
          <HintWrapper
            hint="Sub Total will not display."
          >
            {!showSubtotal && (
              <FontAwesomeIcon
                icon={faEyeSlash}
                style={{
                  color: '#8c8c8e', fontSize: '12px', marginRight: '4px', verticalAlign: 'middle',
                }}
              />
            )}
          </HintWrapper>

          {formatMoney(subtotal)}
        </Text>
      </ListItem>

      {header && (
        <Text color="bodyFontLightColor" fontSize="small" lineHeight="1.3" paddingY="large">
          {header}
        </Text>
      )}

      <SortableList.Dropzone droppableId={`Section-${id}`}>
        <Box flexDirection="column">
          {!sortedCampaignQuoteItemIds.length && (
            <ListItem
              boxProps={{
                alignItems: 'center',
                backgroundColor: 'white',
                borderBottom: '1px dashed',
                borderColor: 'lineColor',
                paddingY: 'medium',
              }}
              onClick={() => addOrderableItem()}
              showAngleIcon
            >
              <Checkbox checked={false} iconDefault={faTimesCircle} />

              <Box flexDirection="column">
                <Text color="bodyFontColor" fontSize="small" lineHeight="1.3">
                  No items added to section
                </Text>
              </Box>

              <Text color="bodyFontLightColor" fontSize="small" lineHeight="1.3" marginLeft="auto">
                Add
              </Text>
            </ListItem>
          )}

          {sortedCampaignQuoteItemIds.map((quoteItemId, itemIndex) => {
            const quoteItem = campaignQuoteItems[quoteItemId] || {}

            return (
              <SortableList.Item draggableId={`Item-${quoteItem.id}`} key={quoteItem.id} index={itemIndex}>
                {/*
                 // @ts-expect-error TS2739 */}
                <CampaignQuoteItem
                  // callbacks={callbacks}
                  campaignQuoteItem={quoteItem}
                  hideDates={hideDates}
                  hidePrices={hidePrices}
                  key={quoteItem.id}
                />
              </SortableList.Item>
            )
          })}
        </Box>
      </SortableList.Dropzone>

      {footer && (
        <Text color="bodyFontLightColor" fontSize="small" lineHeight="1.3" paddingY="large">
          {footer}
        </Text>
      )}

      <Box borderTop="1px dashed" borderColor="lineColor" flexDirection={['column', 'row']} paddingTop="large">
        <Box width={['100%', 'auto']}>
          {!disable_section_editing && (
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => handleCallbackAction({
                callbacks: {
                  // @ts-expect-error TS2322
                  action: addSectionAtIndex,
                  buildActionParams: () => [{ campaignQuoteSectionIds, index, lastIndex }],
                },
                toastText: 'Quote Section Created',
              })}
              size="small"
              width="auto"
            >
              New Section
            </Button>
          )}
        </Box>

        <Box marginTop={['medium', 0]} marginLeft="auto" width={['100%', 'auto']}>
          {!disable_custom_items && (
            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => addCustomItem()}
              marginRight="medium"
              size="small"
              width="auto"
            >
              Add Custom Item
            </Button>
          )}

          <Button
            buttonStyle="primaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addOrderableItem()}
            size="small"
            width="auto"
          >
            Add Order Item
          </Button>
        </Box>
      </Box>
    </Box>
  )
})

export default CampaignQuoteSection
